import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A modern three-blade wind turbine: a tall tapered white tower, a nacelle box
// on top and three blades that turn as one node ("rotor") about the hub. The
// hub stands proud of the nacelle on +Z so the blades sweep in the XY plane and
// read as a turbine from the game's 45-degree camera.
const TOWER = "#eef0f2";
const NACELLE = "#d3d6db";
const HUB = "#b8bcc4";
const BLADE = "#f4f6f8";

const HUB_Y = 1.78;   // hub height on the finished tower
const HUB_Z = 0.2;    // how far the hub stands proud of the nacelle front

function blades(): Part[] {
  const out: Part[] = [
    { geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.12, 10), color: HUB,
      position: [0, HUB_Y, HUB_Z], rotation: [Math.PI / 2, 0, 0], name: "rotor" },
  ];
  for (let i = 0; i < 3; i++) {
    const ang = i * (Math.PI * 2 / 3) + Math.PI / 2;
    // Tapered blade: wider at the root, narrowing to the tip.
    out.push({
      geometry: new THREE.BoxGeometry(0.1, 0.8, 0.03), color: BLADE,
      position: [Math.cos(ang) * 0.45, HUB_Y + Math.sin(ang) * 0.45, HUB_Z + 0.04],
      rotation: [0, 0, ang - Math.PI / 2], name: "rotor",
    });
  }
  return out;
}

export const windTurbine = defineModel({
  id: "wind-turbine",
  batchId: "electric",
  name: "Wind turbine",
  tags: ["building", "electric", "power", "energy", "wind", "renewable", "animated"],
  summary: "A three-blade wind turbine whose rotor turns to make electricity.",
  footprint: 0.55,
  parts(): Part[] {
    return [
      { geometry: new THREE.BoxGeometry(0.46, 0.12, 0.46), color: "#9a9a9a", position: [0, 0.06, 0] },
      { geometry: new THREE.CylinderGeometry(0.16, 0.2, 0.1, 12), color: "#c8cacd", position: [0, 0.17, 0] },
      { geometry: new THREE.CylinderGeometry(0.08, 0.13, 1.55, 12), color: TOWER, position: [0, 1.0, 0] },
      // Nacelle: a chunky box, long axis along Z so the hub pokes out the front.
      { geometry: new THREE.BoxGeometry(0.22, 0.2, 0.5), color: NACELLE, position: [0, HUB_Y, -0.06] },
      { geometry: new THREE.BoxGeometry(0.16, 0.12, 0.1), color: "#9aa0a8", position: [0, HUB_Y + 0.14, -0.18] },
      ...blades(),
    ];
  },
  animations: [
    {
      id: "spin",
      label: "Rotor turning",
      duration: 5,
      loop: true,
      tracks: [
        {
          target: "rotor",
          pivot: [0, HUB_Y, HUB_Z],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 5, value: [0, 0, -Math.PI * 2] },
          ],
        },
      ],
    },
  ],
});
